import { useNavigate } from 'react-router-dom'
import { IDealerChangedEvent, IDropDownValue, IPageProps, IRootState, ICustomColumn, TypeEnum } from 'types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { CaseDto } from '@v1CasesApi'
import { V1ApiCasesClient, V1ApiDealersClient } from 'core/http-common'
import toast from 'core/toast'
import { useDispatch } from 'react-redux'
import { startLoadingAction, endLoadingAction } from 'state/actions/appActions'
import { AppDispatch } from 'state/store'
import { useSelector } from 'react-redux'
import { FormElement, Field, Form, FormClassComponent, } from '@progress/kendo-react-form'
import { DeletedStateEnum, DealerContactDto } from '@v1DealersApi'
import { PageHeader, ReturnButton, FormInput, FormTextArea, FormDatePicker, AddButton, SaveButton, DefaultButton } from 'components'
import DealerSelect from 'components/Dealers/DealerSelect'
import { RequiredValidator } from 'core/Validators'
import { Row, Col, Modal, Button } from 'react-bootstrap'
import SAGrid, { GetDataResponse, GridGetDataProps } from 'components/Grid/SAGrid'
import FormCustomField from 'components/Form/FormCustomField'
import TypeComboBox, { ITypeComboBoxRef } from 'components/ComboBox/TypeComboBox'
import moment from 'moment'
import CasesGrid from 'components/Cases/CasesGrid'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CUSTOMER_SERVICE_USER_ID, SALES_ROLE_ID } from 'core/consts'

const CasesAddPage = ({ title }: IPageProps) => {

    const redirectUrl = '/cases'

    const formRef = useRef<FormClassComponent>(null)
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const [errors, setErrors] = useState<Array<string>>([])
    const [showDealerContactModal, setShowDealerContactModal] = useState<boolean>(false)

    const currentUser = useSelector((state: IRootState) => state.user)
    // Default to Customer Service User for when Sales Person role is adding a case, can be manually overwritten in the form by user
    const currentUserId = useMemo(() => {
        return currentUser.roleId === SALES_ROLE_ID ? CUSTOMER_SERVICE_USER_ID : currentUser.userId
    }, [currentUser.roleId, currentUser.userId])

    const [caseUserOptions, setCaseUserOptions] = useState<IDropDownValue[]>([])
    const [openCases, setOpenCases] = useState<Array<CaseDto>>([])
    const [selectedDealerId, setSelectedDealerId] = useState<number>()
    const [defaultDeletedState, setDefaultDeletedState] = useState<DeletedStateEnum>(DeletedStateEnum.Active)
    const [formState, setFormState] = useState({})

    const [statusId, setStatusId] = useState<number | undefined | null>(1)
    const [reasonId, setReasonId] = useState<number | undefined | null>(undefined)

    const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true)
    const [defaultDealerContactId, setDefaultDealerContactId] = useState<number | undefined | null>(undefined)

    const caseColumns: ICustomColumn[] = [
        { field: 'age', title: 'Age', filterType: 'date', className: 'case-age-cell', headerClassName: 'case-age-header' },
        { field: 'lastModifiedDate', title: 'Last Modified', format: '{0:g}', filterType: 'date' },
        { field: 'caseId', title: 'Case', sortable: true, filterType: 'numeric' },
        { field: 'assignedToUser', title: 'Owner', filterType: 'text' },
        { field: 'subject', title: 'Subject', filterType: 'text', className: 'case-subject-cell', headerClassName: 'case-subject-header' },
        { field: 'caseStatus', title: 'Status', filterType: 'text' },
    ]

    // Called when a user selects a dealer in the Dealer Dropdown.
    const fetchCases = async (props: GridGetDataProps): Promise<GetDataResponse> => {
        if (!selectedDealerId) {
            return {
                data: [],
                total: 0
            }
        }
        try {
            const result = await V1ApiCasesClient.getCasesByDealerId(
                selectedDealerId,
                DeletedStateEnum.Active,
                '',
                undefined,
                undefined,
                props.paging && props.paging.currentPage ? props.paging.currentPage : 1,
                props.paging ? props.paging.take : 10,)

            result.data = result.data.map((item: CaseDto) => {
                item.owner = caseUserOptions.find(x => x.id === item.assignedToUserId)?.text
                // item.status = statusOptions.find(s => s.id === item.caseStatusId)?.text
                return item // Explicitly return the modified item

            })

            return {
                data: result.data,
                total: result.totalItems ? result.totalItems : 0
            }

        }
        catch (e) {
            toast.responseErrors(e)
            return {
                data: [],
                total: 0
            }

        }
    }

    useEffect(() => {
        setFormState({
            caseStatusId: 1,
            caseStatus1: 'New',
            caseOriginId: 4,
            caseOrigin1: 'Internal',
            casePriorityId: 3,
            casePriority1: 'Medium'
        })

        const queryParams = new URLSearchParams(window.location.search)
        const dealerId = queryParams.get('dealerid')
        const deletedState = queryParams.get('deletedstate')
        const id = queryParams.get('id')

        if (dealerId) {

            setIsButtonDisabled(false)
            setSelectedDealerId(Number(dealerId))
        } else if (id) {

            setIsButtonDisabled(false)
            setSelectedDealerId(Number(id))
        }


        if (deletedState)
            setDefaultDeletedState(
                deletedState.toLowerCase() === 'deleted'
                    ? DeletedStateEnum.Deleted
                    : DeletedStateEnum.Active
            )
    }, [])

    const handleDealerContactModal = () => {
        setShowDealerContactModal(!showDealerContactModal)
    }

    const handleDealerChange = async (e: IDealerChangedEvent) => {
        formRef.current?.valueSetter('dealerId', e.dealerId)
        setSelectedDealerId(e.dealerId)
        setIsButtonDisabled(false)
    }

    const getDefaultDealerContact = async () => {
        let contacts = await dealerContactComboRef.current?.fetchData()
        let defaultContact = contacts?.find((c) => !c.text.startsWith('AEM Contact'))

        if (defaultContact) {
            setDefaultDealerContactId(defaultContact.id)
            return defaultContact
        }
        else {
            setDefaultDealerContactId(null)
            formRef.current?.valueSetter('dealerContact', undefined)
            formRef.current?.valueSetter('dealerContactId', undefined)
        }
    }

    useEffect(() => {
        getDefaultDealerContact()
    }, [selectedDealerId])

    const handleDealerClear = async () => {
        setOpenCases([])
        setSelectedDealerId(undefined)
    }

    const handleSubmit = async (values: CaseDto) => {
        try {
            await dispatch(startLoadingAction())

            values.dueDate = values.dueDate ? moment(values.dueDate).startOf('day').toDate() : undefined

            if (selectedDealerId) {
                values.dealerId = selectedDealerId
            }

            var result = await V1ApiCasesClient.createCase(values)

            if (result.success) {
                toast.success('Case was successfully created.', {
                    onOpen: function () {
                        navigate(`/cases/edit/${result.payload?.caseId}`)
                    }
                })
            }

        } catch (e) {
            toast.responseErrors(e)
        }
        finally {
            await dispatch(endLoadingAction())
        }
    }

    const dealerContactComboRef = useRef<ITypeComboBoxRef>(null)
    const [dealerContactKey, setDealerContactKey] = useState<number>(0)
    const [newContact, setNewContact] = useState<DealerContactDto>()

    const handleAddDealerContactSubmit = async (values: DealerContactDto) => {
        try {
            await dispatch(startLoadingAction())

            if (selectedDealerId) {
                values.dealerId = selectedDealerId
            }

            var result = await V1ApiDealersClient.createContact(values)

            if (result.success) {
                toast.success('Dealer Contact was successfully created.')
                setShowDealerContactModal(false)

                setNewContact(result.payload)
                dealerContactComboRef.current?.fetchData()
            } else {
                toast.error(result.errors[0])
            }

        } catch (e) {
            toast.responseErrors(e)
        } finally {
            await dispatch(endLoadingAction())
        }
    }

    useEffect(() => {
        if (newContact) {
            formRef.current?.valueSetter('dealerContact', `${newContact.firstName} ${newContact.lastName} - ${newContact.phone} - ${newContact.email}`)
            formRef.current?.valueSetter('dealerContactId', newContact.dealerContactId)
            setDealerContactKey(prev => prev + 1)
        }
    }, [newContact])

    const handleScroll = () => {
        const element = document.getElementById('OpenCasesSection')
        element?.scrollIntoView({ behavior: 'smooth' })
    }

    return (
        <>
            <div id='CaseForm'>
                <PageHeader title={title}>
                    <DefaultButton disabled={isButtonDisabled} title={isButtonDisabled ? 'Select a Dealer first' : 'View Open Cases'} variant='default' onClick={handleScroll} className='text-nowrap me-1 btn-lg btnWidth'><FontAwesomeIcon icon={['fas', 'search']} />{' '}View Open Cases</DefaultButton>
                    <ReturnButton redirectUrl={redirectUrl} />
                </PageHeader>
                <div className='bg-light border p-3'>
                    <Form
                        onSubmit={(e) => handleSubmit(e as CaseDto)}
                        ref={formRef}
                        initialValues={formState}
                        key={JSON.stringify(formState)}
                        // ignoreModified={true}
                        render={(formRenderProps) => (
                            <FormElement>
                                <Row>
                                    <Col md={4} className='mb-3 mb-md-0'>
                                        <Field
                                            className='form-control'
                                            dataItemKey='id'
                                            textField='text'
                                            name='dealer'
                                            label='Dealer:'
                                            required
                                            dealerId={selectedDealerId ?? 0}
                                            component={DealerSelect}
                                            onDealerCleared={handleDealerClear}
                                            onDealerChanged={handleDealerChange}
                                            showActiveToggle={true}
                                            defaultDeletedState={defaultDeletedState}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Field
                                            component={FormCustomField}
                                            name='firstName'
                                            label='Assigned To:'
                                            onChange={(value: IDropDownValue) => {
                                                formRef.current?.valueSetter('firstName', value && value.text ? value.text : undefined)
                                                formRef.current?.valueSetter('assignedToUserId', value && value.id ? value.id : undefined)
                                            }}
                                            children={
                                                <TypeComboBox
                                                    placeholder='Select'
                                                    typeEnum={TypeEnum.activeUser}
                                                    defaultId={currentUserId}
                                                />
                                            }
                                        />

                                    </Col>
                                    <Col md={4}>
                                        <Field
                                            label='Subject:'
                                            name='subject'
                                            validator={RequiredValidator}
                                            required
                                            component={FormInput}
                                        />
                                    </Col>
                                    <Col md={12}>
                                        <Field
                                            label='Description:'
                                            name='description'
                                            component={FormTextArea}
                                            rows={4}
                                        />
                                    </Col>
                                    <Col md={6} className='mb-3 mb-md-0'>
                                        <Field
                                            label='Due Date:'
                                            name='dueDate'
                                            component={FormDatePicker}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Field
                                            component={FormCustomField}
                                            name='caseStatus1'
                                            label='Status:'
                                            required
                                            validator={RequiredValidator}
                                            onChange={(value: IDropDownValue) => {
                                                formRef.current?.valueSetter('caseStatus1', value && value.text ? value.text : undefined)
                                                formRef.current?.valueSetter('caseStatusId', value && value.id ? value.id : undefined)
                                                setStatusId(value && value.id ? value.id : undefined)
                                                //Clear out substatus
                                                formRef.current?.valueSetter('caseSubStatus1', undefined)
                                                formRef.current?.valueSetter('caseSubStatusId', undefined)

                                            }}
                                            children={
                                                <TypeComboBox
                                                    placeholder='Select'
                                                    typeEnum={TypeEnum.caseStatus}
                                                />
                                            }
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Field
                                            component={FormCustomField}
                                            name='caseSubStatus1'
                                            label='Sub Status:'
                                            disabled={!statusId}
                                            onChange={(value: IDropDownValue) => {
                                                formRef.current?.valueSetter('caseSubStatus1', value && value.text ? value.text : undefined)
                                                formRef.current?.valueSetter('caseSubStatusId', value && value.id ? value.id : undefined)
                                            }}
                                            children={
                                                <TypeComboBox
                                                    placeholder='Select'
                                                    typeParameterId={statusId ? statusId : undefined}
                                                    typeEnum={TypeEnum.caseSubStatus}
                                                />
                                            }
                                        />
                                    </Col>
                                    <Col md={6} className='mb-3 mb-md-0'>
                                        <Field
                                            component={FormCustomField}
                                            name='caseType1'
                                            label='Type:'
                                            required
                                            validator={RequiredValidator}
                                            onChange={(value: IDropDownValue) => {
                                                formRef.current?.valueSetter('caseType1', value && value.text ? value.text : undefined)
                                                formRef.current?.valueSetter('caseTypeId', value && value.id ? value.id : undefined)

                                            }}
                                            children={
                                                <TypeComboBox
                                                    placeholder='Select'
                                                    typeEnum={TypeEnum.caseType}
                                                />
                                            }
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Field
                                            component={FormCustomField}
                                            name='caseReason1'
                                            label='Reason:'
                                            required
                                            validator={RequiredValidator}
                                            onChange={(value: IDropDownValue) => {
                                                formRef.current?.valueSetter('caseReason1', value && value.text ? value.text : undefined)
                                                formRef.current?.valueSetter('caseReasonId', value && value.id ? value.id : undefined)
                                                setReasonId(value && value.id ? value.id : undefined)
                                                //Clear out substatus
                                                formRef.current?.valueSetter('caseSubReason1', undefined)
                                                formRef.current?.valueSetter('caseSubReasonId', undefined)

                                            }}
                                            children={
                                                <TypeComboBox
                                                    placeholder='Select'
                                                    typeEnum={TypeEnum.caseReason}
                                                />
                                            }
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Field
                                            component={FormCustomField}
                                            name='caseSubReason1'
                                            label='Sub Reason:'
                                            disabled={!reasonId}
                                            onChange={(value: IDropDownValue) => {
                                                formRef.current?.valueSetter('caseSubReason1', value && value.text ? value.text : undefined)
                                                formRef.current?.valueSetter('caseSubReasonId', value && value.id ? value.id : undefined)
                                            }}
                                            children={
                                                <TypeComboBox
                                                    placeholder='Select'
                                                    typeParameterId={reasonId ? reasonId : undefined}
                                                    typeEnum={TypeEnum.caseSubReason}
                                                />
                                            }
                                        />
                                    </Col>
                                    <Col md={6} className='mb-3 mb-md-0'>
                                        <Field
                                            component={FormCustomField}
                                            name='caseOrigin1'
                                            label='Origin:'
                                            required
                                            validator={RequiredValidator}
                                            onChange={(value: IDropDownValue) => {
                                                formRef.current?.valueSetter('caseOrigin1', value && value.text ? value.text : undefined)
                                                formRef.current?.valueSetter('caseOriginId', value && value.id ? value.id : undefined)

                                            }}
                                            children={
                                                <TypeComboBox
                                                    placeholder='Select'
                                                    typeEnum={TypeEnum.caseOrigin}
                                                />
                                            }
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Field
                                            component={FormCustomField}
                                            name='casePriority1'
                                            label='Priority:'
                                            required
                                            validator={RequiredValidator}
                                            onChange={(value: IDropDownValue) => {
                                                formRef.current?.valueSetter('casePriority1', value && value.text ? value.text : undefined)
                                                formRef.current?.valueSetter('casePriorityId', value && value.id ? value.id : undefined)

                                            }}
                                            children={
                                                <TypeComboBox
                                                    placeholder='Select'
                                                    typeEnum={TypeEnum.casePriority}
                                                />
                                            }
                                        />
                                    </Col>
                                    <Col md={12}>
                                        <Row>
                                            <Col md={8}>
                                                <Field
                                                    key={dealerContactKey}
                                                    component={FormCustomField}
                                                    name='dealerContact'
                                                    label='Dealer Contact:'
                                                    disabled={selectedDealerId === undefined}
                                                    validator={RequiredValidator}
                                                    required
                                                    onChange={(value: IDropDownValue) => {
                                                        formRef.current?.valueSetter('dealerContact', value?.text)
                                                        formRef.current?.valueSetter('dealerContactId', value?.id)
                                                    }}
                                                    children={
                                                        <TypeComboBox
                                                            ref={dealerContactComboRef}
                                                            placeholder='Select'
                                                            typeEnum={TypeEnum.dealerContact}
                                                            typeParameterId={selectedDealerId}
                                                            defaultId={defaultDealerContactId}
                                                        />
                                                    }
                                                />
                                            </Col>
                                            <Col md={4} style={{ alignSelf: 'end' }}>
                                                <DefaultButton variant='default' onClick={handleDealerContactModal} disabled={!selectedDealerId} className='text-nowrap w-100'>Add Contact</DefaultButton>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col md={12}>
                                        <Field
                                            label='Note:'
                                            name='note'
                                            component={FormTextArea}
                                            rows={4}
                                        />
                                    </Col>
                                </Row>
                                <Row className='my-4'>
                                    <Col xs={12} sm={4}>
                                        <AddButton disabled={!formRenderProps.allowSubmit} type='submit'>Add Case</AddButton>
                                    </Col>
                                </Row>
                            </FormElement>
                        )
                        }
                    />
                </div>

                <Row className='mt-3' id='OpenCasesSection'>
                    <Col>
                        {(selectedDealerId !== undefined && selectedDealerId > 0) &&
                            <CasesGrid
                                title='Open Cases'
                                showOpenCases={true}
                                showClosedCases={false}
                                dealerId={selectedDealerId}
                                hideCaseActions={true}
                                forDealerEditPage={true}
                                useSimpleSearch={true}
                                customColumns={caseColumns}
                            />
                        }
                    </Col>
                </Row>

                <Modal show={showDealerContactModal} onHide={handleDealerContactModal}>
                    <Modal.Header closeButton={showDealerContactModal}>
                        <Modal.Title>Add New Dealer Contact</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form
                            onSubmit={(e) => handleAddDealerContactSubmit(e as DealerContactDto)}
                            render={(formRenderProps) => (
                                <FormElement>
                                    <Row className='mb-3'>
                                        <Col md={12}>
                                            <Field
                                                label='First Name:'
                                                name='firstName'
                                                validator={RequiredValidator}
                                                required
                                                component={FormInput}
                                            />
                                            <Field
                                                label='Last Name:'
                                                name='lastName'
                                                component={FormInput}
                                            />
                                            <Field
                                                label='Email:'
                                                name='email'
                                                component={FormInput}
                                            />
                                            <Field
                                                label='Primary Phone:'
                                                name='phone'
                                                component={FormInput}
                                            />
                                            <Field
                                                label='Secondary Phone:'
                                                name='secondaryPhone'
                                                component={FormInput}
                                            />
                                            <Field
                                                label='Tertiary Phone:'
                                                name='TertiaryPhone'
                                                component={FormInput}
                                            />
                                        </Col>
                                    </Row>
                                    <Row className='mb-3 justify-content-end'>
                                        <div className='d-flex justify-content-end'>
                                            <Button className='me-1' variant='outline-secondary btn-lg' onClick={() => setShowDealerContactModal(false)}>Cancel</Button>
                                            <SaveButton type='submit' title='Add' disabled={!formRenderProps.allowSubmit} />
                                        </div>
                                    </Row>
                                </FormElement>
                            )
                            }
                        />
                    </Modal.Body>
                </Modal>
            </div>
        </>
    )
}
export default CasesAddPage